import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Languages, Save, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import surveyApi from '../surveyApi';
import { hasOptions } from '../questionTypes';
import { LANGUAGES, labelFor } from '../../i18n/strings';

const SURVEY_FIELDS = [
  { key: 'title', label: 'Title', rows: 1 },
  { key: 'description', label: 'Description', rows: 2 },
  { key: 'instructions_md', label: 'Intro / instructions', rows: 3 },
  { key: 'thank_you_md', label: 'Thank-you message', rows: 2 },
];

const emptyDraft = () => ({ survey: {}, questions: {}, options: {} });

// Every base-language text field a translator could fill in, as [value, translated?].
const fieldStatus = (survey, questions, draft) => {
  const out = [];
  for (const f of SURVEY_FIELDS) if (survey[f.key]) out.push(!!draft.survey[f.key]);
  for (const q of questions) {
    const t = draft.questions[q.id] || {};
    const ct = t.config_text || {};
    out.push(!!t.question_text);
    if (q.help_text) out.push(!!t.help_text);
    (q.config?.points || []).forEach((p) => out.push(!!(ct.points || {})[String(p.value)]));
    for (const k of ['min_label', 'max_label', 'placeholder']) if (q.config?.[k]) out.push(!!ct[k]);
    if (hasOptions(q.question_type)) (q.options || []).forEach((o) => out.push(!!draft.options[o.id]));
  }
  return { done: out.filter(Boolean).length, total: out.length };
};

/**
 * Per-language text for a survey. Question/option identity is shared across
 * languages, so translating never touches answers and is allowed at any time —
 * including after responses exist. Blank fields fall back to the base language.
 */
export default function SurveyTranslationsPanel({ survey, questions }) {
  const base = survey.base_language || 'en';
  const targets = LANGUAGES.filter((l) => l.code !== base);
  const [lang, setLang] = useState(targets[0]?.code);
  const [saved, setSaved] = useState({}); // lang -> draft as stored
  const [draft, setDraft] = useState(emptyDraft());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await surveyApi.adminGetTranslations(survey.id);
      setSaved(d.translations || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load translations');
    } finally {
      setLoading(false);
    }
  }, [survey.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const s = saved[lang];
    setDraft(s ? JSON.parse(JSON.stringify(s)) : emptyDraft());
  }, [saved, lang]);

  const offered = (code) => !!saved[code];
  const status = useMemo(() => fieldStatus(survey, questions, draft), [survey, questions, draft]);

  const setSurveyField = (k, v) => setDraft((d) => ({ ...d, survey: { ...d.survey, [k]: v } }));
  const setQ = (qid, patch) => setDraft((d) => ({
    ...d, questions: { ...d.questions, [qid]: { ...(d.questions[qid] || {}), ...patch } },
  }));
  const setQText = (qid, key, v) => setDraft((d) => {
    const cur = d.questions[qid] || {};
    return { ...d, questions: { ...d.questions, [qid]: { ...cur, config_text: { ...(cur.config_text || {}), [key]: v } } } };
  });
  const setPoint = (qid, value, v) => setDraft((d) => {
    const cur = d.questions[qid] || {};
    const ct = cur.config_text || {};
    return {
      ...d,
      questions: { ...d.questions, [qid]: { ...cur, config_text: { ...ct, points: { ...(ct.points || {}), [String(value)]: v } } } },
    };
  });
  const setOption = (oid, v) => setDraft((d) => ({ ...d, options: { ...d.options, [oid]: v } }));

  const save = async () => {
    try {
      setBusy(true);
      await surveyApi.adminSaveTranslation(survey.id, lang, {
        survey: draft.survey,
        questions: questions.map((q) => ({ question_id: q.id, ...(draft.questions[q.id] || {}) })),
        options: questions.flatMap((q) => (q.options || []).map((o) => ({ option_id: o.id, option_text: draft.options[o.id] || '' }))),
      });
      toast.success(`${labelFor(lang)} saved — respondents can now choose it.`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to save translation');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Stop offering ${labelFor(lang)} and delete its text? Past ${labelFor(lang)} responses are kept.`)) return;
    try {
      setBusy(true);
      await surveyApi.adminDeleteTranslation(survey.id, lang);
      toast.success(`${labelFor(lang)} removed.`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to remove translation');
    } finally {
      setBusy(false);
    }
  };

  if (!lang) return null;

  const input = 'w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100 text-sm';
  const ref = (text) => (text ? <p className="text-xs text-slate-500 mb-1 whitespace-pre-wrap line-clamp-3">{labelFor(base)}: {text}</p> : null);

  let qNumber = 0;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-4 mt-6">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="text-slate-100 font-semibold flex items-center gap-2">
          <Languages className="w-4 h-4 text-amber-400" /> Languages
        </h2>
        <div className="flex items-center gap-1">
          <span className="px-2.5 py-1 rounded text-xs bg-slate-700 text-slate-300">{labelFor(base)} (base)</span>
          {targets.map((l) => (
            <button key={l.code} onClick={() => setLang(l.code)}
              className={`px-2.5 py-1 rounded text-xs font-medium ${
                lang === l.code ? 'bg-amber-600 text-white' : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600'}`}>
              {l.label}{offered(l.code) ? ' ✓' : ''}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-4">
            {offered(lang)
              ? `${labelFor(lang)} is offered to respondents. `
              : `${labelFor(lang)} isn't offered yet — saving makes it available. `}
            {status.done} of {status.total} fields translated; blank fields show in {labelFor(base)}.
            Translating never changes collected answers.
          </p>

          <div className="space-y-3 mb-5">
            {SURVEY_FIELDS.filter((f) => survey[f.key]).map((f) => (
              <div key={f.key}>
                <label className="block text-xs text-slate-400 mb-1">{f.label}</label>
                {ref(survey[f.key])}
                {f.rows === 1 ? (
                  <input value={draft.survey[f.key] || ''} onChange={(e) => setSurveyField(f.key, e.target.value)} className={input} />
                ) : (
                  <textarea rows={f.rows} value={draft.survey[f.key] || ''} onChange={(e) => setSurveyField(f.key, e.target.value)} className={input} />
                )}
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {questions.map((q) => {
              const t = draft.questions[q.id] || {};
              const ct = t.config_text || {};
              const isSection = q.question_type === 'section';
              if (!isSection) qNumber += 1;
              return (
                <div key={q.id} className="rounded border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                  <div className="text-xs text-amber-300">{isSection ? 'Section' : `Question ${qNumber}`}</div>
                  {ref(q.question_text)}
                  <textarea rows={isSection ? 1 : 2} value={t.question_text || ''}
                    onChange={(e) => setQ(q.id, { question_text: e.target.value })} className={input} />
                  {q.help_text && (
                    <div>
                      {ref(q.help_text)}
                      <textarea rows={isSection ? 4 : 2} value={t.help_text || ''}
                        onChange={(e) => setQ(q.id, { help_text: e.target.value })} className={input} />
                    </div>
                  )}
                  {(q.config?.points || []).length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {q.config.points.map((p) => (
                        <label key={p.value} className="block">
                          <span className="block text-xs text-slate-500 mb-1">{p.value} · {p.label}</span>
                          <input value={(ct.points || {})[String(p.value)] || ''}
                            onChange={(e) => setPoint(q.id, p.value, e.target.value)} className={input} />
                        </label>
                      ))}
                    </div>
                  )}
                  {['min_label', 'max_label', 'placeholder'].filter((k) => q.config?.[k]).map((k) => (
                    <label key={k} className="block">
                      <span className="block text-xs text-slate-500 mb-1">{k.replace('_', ' ')} · {q.config[k]}</span>
                      <input value={ct[k] || ''} onChange={(e) => setQText(q.id, k, e.target.value)} className={input} />
                    </label>
                  ))}
                  {hasOptions(q.question_type) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {(q.options || []).map((o) => (
                        <label key={o.id} className="block">
                          <span className="block text-xs text-slate-500 mb-1">{o.option_text}</span>
                          <input value={draft.options[o.id] || ''} onChange={(e) => setOption(o.id, e.target.value)} className={input} />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button onClick={save} disabled={busy}
              className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2 disabled:opacity-60">
              <Save className="w-4 h-4" /> {busy ? 'Saving…' : `Save ${labelFor(lang)}`}
            </button>
            {offered(lang) && (
              <button onClick={remove} disabled={busy}
                className="px-3 py-2 rounded bg-slate-700 hover:bg-rose-800 text-slate-200 flex items-center gap-2 text-sm disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> Stop offering {labelFor(lang)}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
